import { defineComponent as _defineComponent } from 'vue'
import Loading from '@/components/atoms/loading/Loading.vue';
import { formatNumberToMoneyString, parseMoneyToNumber } from '@/utils/formatters';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { beachIcon, claimsCountIcon, declinedAccountsIcon, moneyIcon, newAccountsIcon, notBeachIcon, pendingAccountsIcon, rateLimitIcon, rateLimitIconAlt, rateVariationIcon, renewedAccountsIcon, totalAccountsIcon } from './icons';
import DashboardSection, { DashboardSectionProps } from './section/DashboardSection.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'FacilityDashboard',
  setup(__props) {

const instance = getCurrentInstance();
const proxy = instance && instance.proxy as any;

const icon = `<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.529 9.988a2.502 2.502 0 1 1 5 .191A2.441 2.441 0 0 1 12 12.582V14m-.01 3.008H12M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
</svg>`;

const requests = ref<any[]>([]);
const claims = ref<any[]>([]);
const isLoading = ref<boolean>(false);
const limit = ref<number>(1000);
const currentSort = ref<Record<string, string>>({
  col: 'requests.status',
  dir: 'desc',
});
const filter = ref<any>({
  companyId: proxy.$store.state.companyId || '',
  controlZoneId: '',
  modifyType: -1,
});

const accountCards = computed(() => {
  const total = requests.value.length;
  const newAccounts = requests.value.filter((r: any) => r.status === 10).length;
  const pending = requests.value.filter((r: any) => r.status === 5).length;
  const renewed = requests.value.filter((r: any) => r.status === 1).length;
  const closed = requests.value.filter((r: any) => r.status === 6).length;

  return [
    { label: 'Total cuentas cotizadas', value: String(total), icon: totalAccountsIcon },
    { label: 'Nuevas cuentas cotizadas', value: String(newAccounts), icon: newAccountsIcon },
    { label: 'Cuentas pendientes de cotización', value: String(pending), icon: pendingAccountsIcon },
    { label: 'Cuentas renovadas', value: String(renewed), icon: renewedAccountsIcon },
    { label: 'Cuentas declinadas', value: String(closed), icon: declinedAccountsIcon },
  ];
});

const primasCards = computed(() => {
  const brutOriginal = requests.value.reduce((sum: number, r: any) =>
    sum + (r.requests_details && r.requests_details[0] ? parseMoneyToNumber(r.requests_details[0].original_gross_aon) : 0), 0);
  const brutGenerated = requests.value.reduce((sum: number, r: any) =>
    sum + (r.requests_details && r.requests_details[0] && r.requests_details[0].total_sum_secured ? parseMoneyToNumber(r.requests_details[0].total_sum_secured) : 0), 0);

  return [
    { label: 'Bruto original AON', value: `$${formatNumberToMoneyString(brutOriginal)}`, icon: moneyIcon },
    { label: 'Bruto generado', value: `$${formatNumberToMoneyString(brutGenerated)}`, icon: moneyIcon },
  ];
});

const taxCards = computed(() => ([
  { label: 'Variación de tasa', value: '0.5%', icon: rateVariationIcon },
  { label: 'Límite de tasa', value: '30%', icon: rateLimitIcon },
  { label: 'Tasa acumulada', value: '5%', icon: rateLimitIconAlt },
]));

const infoCards = computed(() => {
  const beachFront = requests.value.filter((r: any) => r.beach_front).length;
  const noBeachFront = requests.value.filter((r: any) => !r.beach_front).length;

  return [
    { label: 'Al frente a la playa', value: String(beachFront), icon: beachIcon },
    { label: 'No al frente a la playa', value: String(noBeachFront), icon: notBeachIcon },
  ];
});

const claimsCards = computed(() => {
  const claimsCount = claims.value.length;
  const paymentAmount = claims.value.reduce((sum: number, c: any) => sum + (c.payment ? parseMoneyToNumber(c.payment) : 0), 0) || 0;

  return [
    { label: 'Cantidad de siniestros', value: String(claimsCount), icon: claimsCountIcon },
    { label: 'Montos pagados de siniestros', value: `$${formatNumberToMoneyString(paymentAmount)}`, icon: moneyIcon },
  ];
});

const getSections = computed(() => {
  const sections: DashboardSectionProps[] = [];
  if (accountCards.value.length) sections.push({ title: 'Cuentas', cards: accountCards.value });
  if (primasCards.value.length) sections.push({ title: 'Primas', cards: primasCards.value });
  //if (taxCards.value.length) sections.push({ title: 'Tasas', cards: taxCards.value });
  if (claimsCards.value.length) sections.push({ title: 'Siniestros', cards: claimsCards.value });
  if (infoCards.value.length) sections.push({ title: 'Información', cards: infoCards.value });
  return sections;
});

async function loadRequests() {
  isLoading.value = true;
  try {
    const response: any = await proxy.$root._post('requests/all', {
      limit: limit.value,
      page: 1,
      order: currentSort.value,
      models: 'companies',
      ...filter.value,
    }) || {};
    requests.value = response.data || [];
  } catch (e) {
    console.error('Error loading requests:', e);
    requests.value = []; 
  } finally {
    isLoading.value = false;
  }
}

async function loadClaims() {
  isLoading.value = true;
  try {
    const response: any = await proxy.$root._post('claims/all', {
      limit: limit.value,
      page: 1,
      order: currentSort.value,
      models: 'companies',
      ...filter.value,
    }) || {};
    claims.value = response.data || [];
  } catch (e) {
    console.error('Error loading claims:', e);
    claims.value = [];
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadRequests();
  loadClaims();
});


return { __sfc: true,instance, proxy, icon, requests, claims, isLoading, limit, currentSort, filter, accountCards, primasCards, taxCards, infoCards, claimsCards, getSections, loadRequests, loadClaims, Loading, DashboardSection }
}

})